<template>
  <teleport to="body">
    <div v-if="visible">
      <div class="export-overlay" @click="onCancel"></div>
      <div class="export-panel">
        <div class="export-header border-bottom">
          <h5 class="panel-title m-0">Export App Group</h5>
          <button class="btn-close-panel" @click="onCancel">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="export-body">
<div class="section-block">
            <div class="label-row">
              <span class="form-label">Export Policies</span>
              <i class="ag-icon ag-icon_table_info small info-icon"></i>
            </div>
            <div class="checkbox-list">
              <div class="form-row select-all-row">
                <Checkbox
                  :modelValue="allSelected"
                  :binary="true"
                  :indeterminate="someSelected && !allSelected"
                  @change="toggleSelectAll($event.target.checked)"
                />
                <label class="input-label select-all-label">Select All</label>
              </div>
              <div
                v-for="rule in rules"
                :key="rule.key"
                class="form-row rule-row"
              >
                <Checkbox
                  :modelValue="exportOptions[rule.key]"
                  :binary="true"
                  @change="exportOptions[rule.key] = $event.target.checked"
                />
                <label class="input-label">{{ rule.label }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="export-footer border-top">
          <button class="btn btn-cancel" @click="onCancel">Cancel</button>
          <button class="btn btn-export" @click="onSave">Export</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, reactive, computed, defineExpose } from 'vue';
import Checkbox from 'primevue/checkbox';
import Swal from 'sweetalert2';
import { useStore } from '@/store';
import { GeneralActionTypes } from '@/store/modules/general/action-types';

const store = useStore();
const visible = ref(false);
let currentAppGroupId = null;

const exportOptions = reactive({
  includeProcess: false,
  includeFileAccess: false,
  includeRegistryAccess: false,
  includeCodeLoad: false,
  includeChildLaunch: false,
});

const rules = [
  { key: 'includeProcess',        label: 'Process' },
  { key: 'includeFileAccess',     label: 'File Access' },
  { key: 'includeRegistryAccess', label: 'Registry Access' },
  { key: 'includeCodeLoad',       label: 'Code Load' },
  { key: 'includeChildLaunch',    label: 'Child Launch' },
];

const ruleKeys = rules.map((r) => r.key);

const allSelected = computed(() => ruleKeys.every((k) => exportOptions[k]));
const someSelected = computed(() => ruleKeys.some((k) => exportOptions[k]));

const toggleSelectAll = (checked) => {
  ruleKeys.forEach((k) => (exportOptions[k] = checked));
};

const open = (appGroupId) => {
  currentAppGroupId = appGroupId;
  ruleKeys.forEach((k) => (exportOptions[k] = true));
  visible.value = true;
};

const onCancel = () => {
  visible.value = false;
};

const onSave = async () => {
  try {
    const payload = {
      AppGroupId: currentAppGroupId,
      IncludeProcess: exportOptions.includeProcess,
      IncludeFileAccess: exportOptions.includeFileAccess,
      IncludeRegistryAccess: exportOptions.includeRegistryAccess,
      IncludeCodeLoad: exportOptions.includeCodeLoad,
      IncludeChildLaunch: exportOptions.includeChildLaunch,
    };

    const blob = await store.dispatch(GeneralActionTypes.EXPORT_APPGROUP, payload);

    if (blob) {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const fileName = `export_${day}_${month}_${year}_appgroup_${currentAppGroupId}.json`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire('Exported!', 'App Group exported successfully.', 'success');
      onCancel();
    } else {
      Swal.fire('Error', 'Export failed.', 'error');
    }
  } catch (err) {
    console.error(err);
    Swal.fire('Error', err?.message || 'Something went wrong while exporting.', 'error');
  }
};

defineExpose({ open });
</script>

<style scoped>
.export-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1040;
}
.export-panel {
  position: fixed;
  top: 0; right: 0;
  height: 100%;
  width: 550px;
  background-color: #fff;
  z-index: 1050;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
  animation: slideInRight 0.3s ease-out;
  display: flex;
  flex-direction: column;
}
@keyframes slideInRight {
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
}
.export-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}
.panel-title { color: #164B6C; font-weight: bold; }
.btn-close-panel {
  background: transparent; border: none; cursor: pointer;
  font-size: 1rem; color: #000;
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.btn-close-panel:hover { background-color: #e6f0fb; }
.export-body { flex: none; overflow-y: auto; padding: 1rem 0; }
.export-footer { padding: 1rem; display: flex; justify-content: flex-end; gap: 0.5rem; }
.section-block {
  display: flex; flex-direction: column; gap: 8px;
  padding-left: 1rem; padding-right: 1rem; font-size: 0.8rem;
}
.label-row { display: flex; align-items: center; gap: 4px; }
.form-label { color: #164b6c; font-weight: bold; margin-left: 8px; }
.checkbox-list { display: flex; flex-direction: column; gap: 8px; padding-left: 8px; }
.form-row { display: flex; align-items: center; }
.select-all-row { padding-bottom: 6px; }
.rule-row { padding-left: 20px; }
.input-label { margin-left: 10px; font-size: 0.758rem; cursor: pointer; }
.select-all-label { font-weight: bold; }
.btn-cancel { border: 1px solid #D2D9E2; width: 30%; }
.btn-export { background-color: #003060; color: #fff; width: 30%; }
.btn-export:hover { filter: brightness(1.08); }
::v-deep(.p-checkbox .p-checkbox-box) { border: 1px solid #3377B8 !important; width: 18px; height: 18px; }
::v-deep(.p-checkbox.p-highlight .p-checkbox-box) { border-color: #3b82f6 !important; background: #3b82f6 !important; }
::v-deep(.p-checkbox .p-checkbox-box .p-checkbox-icon) { font-size: 12px; }
</style>